import { LitElement, html, css, PropertyValueMap } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { bindEvent, Handler } from '@gsmlg/utils/dist/browser/bindEvent';
import '../icons/bars';

@customElement('app-bar')
class AppBarElement extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      color: var(--app-bar-color, #fff);
      background-color: var(--app-bar-background-color, #fc125c);
      width: 100%;
      height: var(--app-bar-height, 3.5rem);
    }
    .container {
      width: 100%;
    }
    header {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      width: 85%;
      color: var(--app-bar-menu-color, inherit);
      background-color: var(--app-bar-menu-background-color, inherit);
    }
    header.menu {
      position: relative;
    }
    header.menu .menu-is-open {
      transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
      position: absolute;
      width: 100%;
      color: var(--app-bar-menu-color, inherit);
      background-color: var(--app-bar-menu-background-color, inherit);
      top: var(--app-bar-height, 3.5rem);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }
    header.menu .menu-is-open > slot,
    header.menu .menu-is-open > slot > * {
      width: 100%;
      color: var(--app-bar-menu-item-color, inherit);
      background-color: var(--app-bar-menu-item-background-color, inherit);
      display: flex;
      flex-direction: column;
      flex: 1 1 auto;
      justify-content: flex-start;
      align-items: center;
      text-align: center;
    }
    .p-l,
    .p-r {
      display: flex;
      flex-direction: row;
      align-items: center;
    }
    .p-l {
      justify-content: flex-start;
    }
    .p-r {
      justify-content: flex-end;
    }
    .band {
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      align-items: center;
    }
    .band > h1 {
      margin: 0;
      padding: 0;
      font-size: 1.75rem;
      display: none;
      color: var(--app-bar-name-color, inherit);
    }
    .nav-btn {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      width: var(--app-bar-height, 3.5rem);
      height: var(--app-bar-height, 3.5rem);
    }
    .nav-btn > icon-bars {
      cursor: pointer;
    }
    .nav {
      margin-left: 1.3rem;
      display: none;
      flex-direction: row;
      justify-content: flex-start;
      align-items: center;
    }
    .actions,
    .user {
      justify-self: flex-end;
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      align-items: center;
      margin: 0 0.618rem;
    }
    @media only screen and (min-width: 768px) {
      .nav {
        display: flex;
      }
      .nav-btn {
        display: none;
      }
    }
    @media only screen and (min-width: 992px) {
      .band h1 {
        display: inline-flex;
      }
      .container {
        width: 960px;
      }
    }
    @media only screen and (min-width: 1200px) {
      .container {
        width: 1140px;
      }
    }
    @media only screen and (min-width: 1400px) {
      .container {
        width: 1320px;
      }
    }
  `;

  @property({ type: Boolean, attribute: 'fixed', reflect: true })
  fixed: boolean = false;

  @property({ type: Boolean, attribute: 'hidename', reflect: true })
  hidename: boolean = false;

  @property({ type: String, attribute: 'app-name', reflect: true })
  appName: string = 'GSMLG.DEV';

  @property({ type: String, attribute: 'color', reflect: true })
  color: string | undefined = undefined;

  @property({ type: String, attribute: 'background-color', reflect: true })
  backgroundColor: string | undefined = undefined;

  _elHandler?: Handler = undefined;

  isMobile: boolean = false;

  menuIsOpen: boolean = false;

  constructor() {
    super();

    this.toggleMenuOpen.bind(this);
    this._setColor();
  }

  override connectedCallback() {
    super.connectedCallback();
    this._elHandler = this.handleMenuWhenMQChange();
  }

  override disconnectedCallback() {
    if (this._elHandler) {
      this._elHandler.release();
    }

    super.disconnectedCallback();
  }

  protected override firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties);
    this._setColor();
  }

  override attributeChangedCallback(
    name: string,
    old: string | null,
    value: string | null,
  ) {
    super.attributeChangedCallback(name, old, value);
    if (name === 'fixed') {
      if (this.fixed) {
        this.style.position = 'fixed';
        this.style.top = '0px';
        this.style.left = '0px';
        this.style.right = '0px';
      } else {
        this.style.position = '';
        this.style.top = '';
        this.style.left = '';
        this.style.right = '';
      }
    }
    if (name === 'color') {
      this.style.color = this.color as string;
    }
    if (name === 'background-color') {
      this.style.backgroundColor = this.backgroundColor as string;
    }
    this.requestUpdate();
  }

  override render() {
    return html`
      <header class=${classMap({ menu: this.menuIsOpen, container: true })}>
        <div class="p-l">
          ${this.isMobile
            ? html`
                <div class="nav-btn">
                  <icon-bars @click=${this.toggleMenuOpen} />
                </div>
              `
            : ''}
          <div class="band">
            <slot name="logo"></slot>
            ${this.hidename ? '' : html`<h1>${this.appName}</h1>`}
          </div>
          ${this.isMobile
            ? ''
            : html`
                <div class="nav">
                  <slot name="nav"></slot>
                </div>
              `}
        </div>
        <div class="p-r">
          <div class="actions">
            <slot name="actions"></slot>
          </div>
          <div class="user">
            <slot name="user"></slot>
          </div>
        </div>
        ${this.menuIsOpen
          ? html`
              <div class=${classMap({ 'menu-is-open': this.menuIsOpen })}>
                <slot name="nav"></slot>
              </div>
            `
          : ''}
      </header>
    `;
  }

  private _setColor() {
    if (this.color) {
      this.style.color = this.color;
    }
    if (this.backgroundColor) {
      this.style.backgroundColor = this.backgroundColor;
    }
  }

  private handleMenuWhenMQChange() {
    this.isMobile = !window.matchMedia('(min-width: 768px)').matches;
    return bindEvent(window.matchMedia('(min-width: 768px)'), 'change', ({ matches }) => {
      if (matches) {
        this.menuIsOpen = false;
        this.isMobile = false;
      } else {
        this.isMobile = true;
      }
      this.requestUpdate();
    });
  }

  private toggleMenuOpen() {
    this.menuIsOpen = !this.menuIsOpen;
    this.requestUpdate();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'app-bar': AppBarElement;
  }
}
